import { useRef, useState } from 'react';
import { Search } from 'lucide-react';
import { Popover, PopoverContent, PopoverTrigger } from '../ui/popover';
import { DateTimePicker } from '../ui/date-time-picker';
import { useAlertSearch } from '@/app/AlertsDashboard/hooks/useAlertSearch';
import { AlertSearchResults } from './AlertSearchResults';
import { isBefore, isAfter } from 'date-fns';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../ui/select';

interface AlertSearchProps {
  currentDate?: Date;
}

type SearchType = 'alert' | 'site' | 'customer';

const searchTypeLabels: Record<SearchType, string> = {
  alert: 'Alert ID',
  site: 'Site',
  customer: 'Customer',
};

export function AlertSearch({ currentDate = new Date() }: AlertSearchProps) {
  const [searchText, setSearchText] = useState('');
  const [searchType, setSearchType] = useState<SearchType>('alert');
  const [isSearchOpen, setIsSearchOpen] = useState(false);
  const [startDate, setStartDate] = useState<Date | undefined>(undefined);
  const [endDate, setEndDate] = useState<Date | undefined>(undefined);
  const inputRef = useRef<HTMLInputElement>(null);

  const { data: searchResults, isLoading } = useAlertSearch({
    searchText,
    searchType,
    startDate,
    endDate,
  });

  const handleInputClick = (e: React.MouseEvent) => {
    e.stopPropagation();
    setIsSearchOpen(true);
  };

  const handleStartDateChange = (date: Date | undefined) => {
    if (date) {
      setStartDate(date);
      if (endDate && isBefore(endDate, date)) {
        setEndDate(undefined);
      }
    } else {
      setStartDate(undefined);
    }
  };

  const handleEndDateChange = (date: Date | undefined) => {
    if (date) {
      if (!startDate || !isBefore(date, startDate)) {
        setEndDate(date);
      }
    } else {
      setEndDate(undefined);
    }
  };

  const isStartDateDisabled = (date: Date) => {
    return isAfter(date, currentDate);
  };

  const isEndDateDisabled = (date: Date) => {
    return startDate ? isBefore(date, startDate) : false;
  };

  const getPlaceholder = () => {
    switch (searchType) {
      case 'alert':
        return 'Enter Alert ID (e.g., A-1234)';
      case 'site':
        return 'Enter site name';
      case 'customer':
        return 'Enter customer name';
      default:
        return 'Search alerts';
    }
  };

  return (
    <Popover open={isSearchOpen} onOpenChange={setIsSearchOpen}>
      <div className="relative">
        <PopoverTrigger asChild>
          <div className="relative">
            <div className="relative flex">
              <Select
                value={searchType}
                onValueChange={(value: SearchType) => setSearchType(value)}
              >
                <SelectTrigger className="absolute left-0 top-0 h-9 w-[110px] rounded-r-none border-r-0 bg-black pl-3 pr-2 text-[#B7D257]">
                  <SelectValue placeholder="Search type" />
                </SelectTrigger>
                <SelectContent>
                  {Object.entries(searchTypeLabels).map(([value, label]) => (
                    <SelectItem key={value} value={value}>
                      {label}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
              <input
                ref={inputRef}
                type="text"
                placeholder={getPlaceholder()}
                value={searchText}
                onChange={(e) => setSearchText(e.target.value)}
                onClick={handleInputClick}
                className="box-border block h-9 w-full rounded-md border bg-black pl-[120px] pr-10 text-xs text-white focus:outline-none focus:ring-2 focus:ring-lime-500/50"
              />
              <Search className="absolute right-3 top-1/2 h-4 w-4 -translate-y-1/2 text-muted-foreground" />
            </div>
          </div>
        </PopoverTrigger>
        <PopoverContent
          className="w-[--trigger-width] p-4"
          align="start"
          onOpenAutoFocus={(e) => {
            e.preventDefault();
          }}
          onInteractOutside={(e) => {
            if (inputRef.current && !inputRef.current.contains(e.target as Node)) {
              setIsSearchOpen(false);
            }
          }}
          style={
            {
              '--trigger-width': inputRef.current ? `${inputRef.current.offsetWidth}px` : '100%',
            } as React.CSSProperties
          }
        >
          <div className="space-y-4">
            <div className="grid grid-cols-2 gap-4">
              {searchType === 'alert' && (
                <>
                  <div>
                    <label className="mb-1 block text-xs text-muted-foreground">Start Date</label>
                    <DateTimePicker
                      value={startDate}
                      onChange={handleStartDateChange}
                      disabledDays={isStartDateDisabled}
                      defaultPopupValue={(() => {
                        const date = new Date();
                        date.setHours(0, 0, 0, 0);
                        return date;
                      })()}
                      granularity="second"
                    />
                  </div>
                  <div>
                    <label className="mb-1 block text-xs text-muted-foreground">End Date</label>
                    <DateTimePicker
                      value={endDate}
                      onChange={handleEndDateChange}
                      disabled={!startDate}
                      disabledDays={isEndDateDisabled}
                      defaultPopupValue={(() => {
                        const date = new Date();
                        date.setHours(23, 59, 59, 999);
                        return date;
                      })()}
                      granularity="second"
                    />
                  </div>
                </>
              )}
            </div>
            <AlertSearchResults
              results={(searchResults?.alerts || []).map((alert) => ({
                id: alert.id!,
                alertId: alert.alertId!,
                companyName: alert.companyName!,
                projectName: alert.projectName!,
                occurred: alert.occurred!,
                status: alert.status!,
                snapshot: alert.snapshot!,
                actionedBy: alert.actionedBy!,
                note: '',
              }))}
              isLoading={isLoading}
              searchText={searchText}
              customerName=""
              siteName=""
              startDate={startDate}
              endDate={endDate}
              searchType={searchType}
              onClose={() => setIsSearchOpen(false)}
            />
          </div>
        </PopoverContent>
      </div>
    </Popover>
  );
}
